import { PhysicalHazard, StateOfMatter, UnitType } from "encamp-prisma/generated/browser";
import { groupBy, sortBy } from "lodash-es";

import { storageTypeFields } from "../utils/storageTypeFields";
import { resolveCombineTargetUnit } from "./combineTargetUnit";
import { canConvertWithoutDensity } from "./unitConversions";

export type CombineTargetProfile = {
  stateOfMatter: readonly StateOfMatter[];
  physicalHazards: readonly PhysicalHazard[];
};

/** One source FacilityChemical-level entry of a combine facility-year. */
export type CombineFacilityYearEntry = {
  unit: UnitType | null;
  maxAmountLargestContainer: number | null;
};

/** One storage-location row of a combine facility-year. */
export type CombineStorageLocationRow = {
  chemicalId: string;
  storageLocationId: string;
  storageType: string | null;
  storageTypes: string[] | null;
  storageTypeDescription: string | null;
  unit: UnitType | null;
  maxAmount: number | null;
  averageAmount: number | null;
  maxAmountLargestContainer: number | null;
};

export type CombineFacilityYear = {
  facilityState: string | null;
  entries: CombineFacilityYearEntry[];
  storageLocationRows: CombineStorageLocationRow[];
};

/** Identity key for rows that may combine: location + storage-type set + description. */
export function combineStorageLocationGroupKey(row: {
  storageLocationId: string;
  storageType?: string | null;
  storageTypes?: string[] | null;
  storageTypeDescription: string | null;
}): string {
  const sortedStorageTypes = [...storageTypeFields(row).storageTypes].sort();
  return `${row.storageLocationId}|${JSON.stringify(sortedStorageTypes)}|${
    row.storageTypeDescription ?? ""
  }`;
}

/**
 * Per merged facility-year, the units every cross-unit COMBINE call site must
 * agree on: `jurisdictionTarget` (what resolveCombineTargetUnit says this
 * facility/state-of-matter/physical-hazard combination reports in),
 * `baseUnit` (the merged FC row's unit — uniform non-null entry units keep
 * that unit; all-null keeps null, since nobody chose one; mixed non-null
 * units fall back to jurisdictionTarget), and `slTargetUnit` (the unit
 * SL-group conversions target — follows baseUnit so a facility-year whose FCs
 * already agree on a unit doesn't get its SL rows reprojected onto the
 * jurisdiction unit).
 */
export function resolveCombineFacilityYearUnits(
  facilityYear: Pick<CombineFacilityYear, "facilityState" | "entries">,
  profile: CombineTargetProfile
): { baseUnit: UnitType | null; jurisdictionTarget: UnitType; slTargetUnit: UnitType } {
  const nonNullUnits = new Set(
    facilityYear.entries.map((entry) => entry.unit).filter((unit): unit is UnitType => unit != null)
  );
  const jurisdictionTarget = resolveCombineTargetUnit(
    facilityYear.facilityState,
    profile.stateOfMatter,
    profile.physicalHazards
  );
  let baseUnit: UnitType | null;
  if (nonNullUnits.size === 0) {
    baseUnit = null;
  } else if (nonNullUnits.size === 1) {
    baseUnit = nonNullUnits.values().next().value ?? null;
  } else {
    baseUnit = jurisdictionTarget;
  }
  const slTargetUnit = baseUnit ?? jurisdictionTarget;
  return { baseUnit, jurisdictionTarget, slTargetUnit };
}

/** True when the FC-level amount sum will convert: entry units are mixed and can't all reach jurisdictionTarget without a density. */
function fcAmountRequiresDensity(
  entries: readonly CombineFacilityYearEntry[],
  jurisdictionTarget: UnitType
): boolean {
  const nonNullUnits = new Set(
    entries.map((entry) => entry.unit).filter((unit): unit is UnitType => unit != null)
  );
  return (
    nonNullUnits.size > 1 &&
    Array.from(nonNullUnits).some((unit) => !canConvertWithoutDensity(unit, jurisdictionTarget))
  );
}

/**
 * True when the FC-level largest-container pick will convert: a multi-entry
 * facility-year has a container candidate (FC-level or SL-level) whose own
 * unit differs from baseUnit and can't reach it without a density.
 */
function fcContainerRequiresDensity(
  facilityYear: CombineFacilityYear,
  baseUnit: UnitType | null
): boolean {
  if (facilityYear.entries.length <= 1 || baseUnit == null) return false;

  const containerUnits = new Set(
    [
      ...facilityYear.entries.map((entry) => ({
        unit: entry.unit,
        value: entry.maxAmountLargestContainer,
      })),
      ...facilityYear.storageLocationRows.map((row) => ({
        unit: row.unit,
        value: row.maxAmountLargestContainer,
      })),
    ]
      .filter((candidate) => candidate.value != null)
      .map((candidate) => candidate.unit)
      .filter((unit): unit is UnitType => unit != null && unit !== baseUnit)
  );
  return Array.from(containerUnits).some((unit) => !canConvertWithoutDensity(unit, baseUnit));
}

/**
 * An SL group's settled unit: the uniform valued-row unit, slTargetUnit when
 * valued rows are mixed, or — for a container-only group with no valued row
 * at all — the first non-null unit in deterministic (chemicalId, then unit)
 * order, matching the tie-break the SL merge itself uses.
 */
function slGroupFinalUnit(
  rows: readonly CombineStorageLocationRow[],
  valuedUnits: ReadonlySet<UnitType>,
  slTargetUnit: UnitType
): UnitType | null {
  const hasValuedRow = rows.some((row) => row.maxAmount != null || row.averageAmount != null);
  if (!hasValuedRow) {
    return (
      sortBy(rows, ["chemicalId", (row) => row.unit ?? ""]).find((row) => row.unit != null)?.unit ??
      null
    );
  }
  if (valuedUnits.size > 1) {
    return slTargetUnit;
  }
  return Array.from(valuedUnits)[0] ?? null;
}

/**
 * True when one SL group needs a density: its valued rows' non-null units
 * are mixed and can't all reach slTargetUnit without one, OR a container
 * row's unit differs from the group's settled unit (see slGroupFinalUnit)
 * and can't reach it without one.
 */
function slGroupRequiresDensity(
  rows: readonly CombineStorageLocationRow[],
  slTargetUnit: UnitType
): boolean {
  const valuedUnits = new Set(
    rows
      .filter((row) => row.maxAmount != null || row.averageAmount != null)
      .map((row) => row.unit)
      .filter((unit): unit is UnitType => unit != null)
  );
  if (
    valuedUnits.size > 1 &&
    Array.from(valuedUnits).some((unit) => !canConvertWithoutDensity(unit, slTargetUnit))
  ) {
    return true;
  }

  const finalUnit = slGroupFinalUnit(rows, valuedUnits, slTargetUnit);
  if (finalUnit == null) {
    return false;
  }

  const containerUnits = new Set(
    rows
      .filter((row) => row.maxAmountLargestContainer != null)
      .map((row) => row.unit)
      .filter((unit): unit is UnitType => unit != null && unit !== finalUnit)
  );
  return Array.from(containerUnits).some((unit) => !canConvertWithoutDensity(unit, finalUnit));
}

/**
 * True when combining this facility-year needs a merged density. A
 * conversion happens only at a site whose own units genuinely differ: the
 * FC-level amount sum, the FC-level container pick, or one SL group (valued
 * rows vs slTargetUnit; container rows vs the group's own settled unit). A
 * density is demanded exactly when some site will convert AND some non-null
 * unit at that site can't reach that site's target without one; null units
 * never trigger a conversion.
 */
export function combineRequiresDensity(
  facilityYear: CombineFacilityYear,
  profile: CombineTargetProfile
): boolean {
  const { baseUnit, jurisdictionTarget, slTargetUnit } = resolveCombineFacilityYearUnits(
    facilityYear,
    profile
  );

  if (fcAmountRequiresDensity(facilityYear.entries, jurisdictionTarget)) {
    return true;
  }
  if (fcContainerRequiresDensity(facilityYear, baseUnit)) {
    return true;
  }

  const slGroups = groupBy(facilityYear.storageLocationRows, (row: CombineStorageLocationRow) =>
    combineStorageLocationGroupKey(row)
  );
  return Object.values(slGroups).some((rows) => slGroupRequiresDensity(rows, slTargetUnit));
}
